import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import {
  ShieldCheck, RefreshCw, FileDown, Eye, Ban, ChevronDown, ChevronRight, Settings2,
} from 'lucide-react';

const QR_TEST = 'https://prewww2.aeat.es/wlpl/TIKE-CONT/ValidarQR';
const QR_PROD = 'https://www2.agenciatributaria.gob.es/wlpl/TIKE-CONT/ValidarQR';

const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

export const FiscalSettings = () => {
  const { t, i18n } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [facturas, setFacturas] = useState([]);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showSoftware, setShowSoftware] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [detail, setDetail] = useState(null);
  const [anularTarget, setAnularTarget] = useState(null);
  const [anularReason, setAnularReason] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });

  const loadAll = useCallback(async () => {
    try {
      const [c, s, f] = await Promise.all([
        api.get('/fiscal/config'),
        api.get('/fiscal/status'),
        api.get('/fiscal/facturas'),
      ]);
      setCfg(c.data);
      setStatus(s.data);
      setFacturas(f.data || []);
    } catch (e) { /* settings page handles unauth */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(i18n.language); } catch (e) { return iso; }
  };

  const setField = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setSoft = (k, v) => setCfg((p) => ({ ...p, software: { ...(p.software || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        serie: cfg.serie,
        nif: cfg.nif,
        razon_social: cfg.razon_social,
        default_tipo: cfg.default_tipo,
        qr_base_url: cfg.qr_base_url,
        software: cfg.software,
      };
      const { data } = await api.put('/fiscal/config', payload);
      setCfg(data);
      toast.success(t('fiscal.saved'));
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('fiscal.saveError'));
    } finally { setSaving(false); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const { data } = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { toast.error(t('common.error')); }
  };

  const exportFactura = (f, format) =>
    downloadBlob(`/fiscal/facturas/${f.id}/export?format=${format}`,
      `factura-${(f.num_serie || '').replace(/\//g, '-')}.${format}`);

  const exportRange = () => {
    const qs = new URLSearchParams({ format: 'xml' });
    if (range.from) qs.set('date_from', range.from);
    if (range.to) qs.set('date_to', range.to);
    downloadBlob(`/fiscal/export?${qs.toString()}`, 'verifactu-export.xml');
  };

  const openDetail = async (f) => {
    try { const { data } = await api.get(`/fiscal/facturas/${f.id}`); setDetail(data); }
    catch (e) { toast.error(t('common.error')); }
  };

  const loadAudit = async () => {
    if (!showAudit) {
      try { const { data } = await api.get('/fiscal/audit'); setAudit(data || []); } catch (e) {}
    }
    setShowAudit((v) => !v);
  };

  const confirmAnular = async () => {
    try {
      await api.post(`/fiscal/facturas/${anularTarget.id}/anular`, { motivo: anularReason });
      toast.success(t('fiscal.anulled'));
      setAnularTarget(null); setAnularReason('');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || t('fiscal.anularError')); }
  };

  if (!cfg) return null;

  const qrEnv = cfg.qr_base_url === QR_PROD ? 'prod' : 'test';

  return (
    <div className="space-y-4" data-testid="fiscal-settings">
      {/* ---------------- Configuration ---------------- */}
      <Card className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <div className="font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> {t('fiscal.title')}
          </div>
          <Badge variant={cfg.enabled ? 'default' : 'secondary'} data-testid="fiscal-state-badge">
            {cfg.enabled ? t('fiscal.enabledBadge') : t('fiscal.disabledBadge')}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t('fiscal.subtitle')}</p>

        <div className="flex items-center justify-between rounded-xl border border-border p-3 mb-4">
          <div>
            <div className="font-medium">{t('fiscal.enable')}</div>
            <div className="text-sm text-muted-foreground">{t('fiscal.enableDesc')}</div>
          </div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setField('enabled', v)} data-testid="fiscal-enable-toggle" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>{t('fiscal.serie')}</Label>
            <Input value={cfg.serie || ''} onChange={(e) => setField('serie', e.target.value)} className="mt-1.5" data-testid="fiscal-serie-input" />
          </div>
          <div>
            <Label>{t('fiscal.defaultType')}</Label>
            <Select value={cfg.default_tipo || 'F2'} onValueChange={(v) => setField('default_tipo', v)}>
              <SelectTrigger className="mt-1.5" data-testid="fiscal-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="F2">{t('fiscal.typeF2')}</SelectItem>
                <SelectItem value="F1">{t('fiscal.typeF1')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('fiscal.nif')}</Label>
            <Input value={cfg.nif || ''} onChange={(e) => setField('nif', e.target.value)} className="mt-1.5 font-mono" data-testid="fiscal-nif-input" />
          </div>
          <div>
            <Label>{t('fiscal.razonSocial')}</Label>
            <Input value={cfg.razon_social || ''} onChange={(e) => setField('razon_social', e.target.value)} className="mt-1.5" data-testid="fiscal-razon-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>{t('fiscal.qrEnv')}</Label>
            <Select value={qrEnv} onValueChange={(v) => setField('qr_base_url', v === 'prod' ? QR_PROD : QR_TEST)}>
              <SelectTrigger className="mt-1.5" data-testid="fiscal-qrenv-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="test">{t('fiscal.qrTest')}</SelectItem>
                <SelectItem value="prod">{t('fiscal.qrProd')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setShowSoftware((v) => !v)}
          className="mt-4 flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
          data-testid="fiscal-software-toggle"
        >
          {showSoftware ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Settings2 className="h-4 w-4" /> {t('fiscal.software')}
        </button>
        {showSoftware && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 rounded-xl border border-border p-3">
            <div><Label>{t('fiscal.softwareName')}</Label><Input value={cfg.software?.nombre || ''} onChange={(e) => setSoft('nombre', e.target.value)} className="mt-1.5" /></div>
            <div><Label>{t('fiscal.softwareNif')}</Label><Input value={cfg.software?.nif || ''} onChange={(e) => setSoft('nif', e.target.value)} className="mt-1.5 font-mono" /></div>
            <div><Label>{t('fiscal.softwareId')}</Label><Input value={cfg.software?.id_sistema || ''} onChange={(e) => setSoft('id_sistema', e.target.value)} className="mt-1.5" /></div>
            <div><Label>{t('fiscal.softwareVersion')}</Label><Input value={cfg.software?.version || ''} onChange={(e) => setSoft('version', e.target.value)} className="mt-1.5" /></div>
            <div><Label>{t('fiscal.softwareInstall')}</Label><Input value={cfg.software?.numero_instalacion || ''} onChange={(e) => setSoft('numero_instalacion', e.target.value)} className="mt-1.5" /></div>
          </div>
        )}

        <div className="mt-4">
          <Button onClick={save} disabled={saving} className="rounded-xl" data-testid="fiscal-save-button">
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </Card>

      {/* ---------------- Status ---------------- */}
      {status && (
        <Card className="p-5">
          <div className="font-semibold mb-4">{t('fiscal.statusTitle')}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-border p-3">
              <div className="text-xs text-muted-foreground">{t('fiscal.totalFacturas')}</div>
              <div className="text-2xl font-display font-extrabold" data-testid="fiscal-total-facturas">{status.total_facturas}</div>
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-xs text-muted-foreground">{t('fiscal.totalAnuladas')}</div>
              <div className="text-2xl font-display font-extrabold text-destructive">{status.total_anuladas}</div>
            </div>
            <div className="rounded-xl border border-border p-3 col-span-2 sm:col-span-1">
              <div className="text-xs text-muted-foreground">{t('fiscal.serie')}</div>
              <div className="text-2xl font-display font-extrabold">{status.serie}</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xs text-muted-foreground mb-1">{t('fiscal.chainTip')}</div>
            <div className="font-mono text-xs break-all rounded-lg bg-muted/50 p-2" data-testid="fiscal-chain-tip">
              {status.chain_tip_huella || '—'}
            </div>
          </div>
        </Card>
      )}

      {/* ---------------- Export range ---------------- */}
      <Card className="p-5">
        <div className="font-semibold mb-1">{t('fiscal.exportRange')}</div>
        <p className="text-sm text-muted-foreground mb-4">{t('fiscal.exportRangeDesc')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>{t('fiscal.from')}</Label><Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="mt-1.5" data-testid="fiscal-export-from" /></div>
          <div><Label>{t('fiscal.to')}</Label><Input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="mt-1.5" data-testid="fiscal-export-to" /></div>
          <Button variant="outline" onClick={exportRange} className="rounded-xl" data-testid="fiscal-export-range-button">
            <FileDown className="h-4 w-4 mr-2" /> {t('fiscal.export')}
          </Button>
        </div>
      </Card>

      {/* ---------------- Facturas table ---------------- */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">{t('fiscal.facturasTitle')}</div>
          <Button variant="outline" size="sm" onClick={loadAll} className="rounded-lg" data-testid="fiscal-refresh-button">
            <RefreshCw className="h-4 w-4 mr-2" /> {t('fiscal.refresh')}
          </Button>
        </div>
        {facturas.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">{t('fiscal.noFacturas')}</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('fiscal.colNum')}</TableHead>
                  <TableHead>{t('fiscal.colType')}</TableHead>
                  <TableHead>{t('fiscal.colDate')}</TableHead>
                  <TableHead className="text-right">{t('fiscal.colTotal')}</TableHead>
                  <TableHead>{t('fiscal.colState')}</TableHead>
                  <TableHead className="text-right">{t('fiscal.colActions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {facturas.map((f) => (
                  <TableRow key={f.id} data-testid={`fiscal-row-${f.numero}`}>
                    <TableCell className="font-mono">{f.num_serie}</TableCell>
                    <TableCell><Badge variant="outline">{f.tipo}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{f.fecha_expedicion}</TableCell>
                    <TableCell className="text-right font-semibold">{eur(f.importe_total)}</TableCell>
                    <TableCell>
                      {f.anulada
                        ? <Badge variant="destructive">{t('fiscal.stateCancelled')}</Badge>
                        : <Badge className="bg-pos-free/15 text-pos-free hover:bg-pos-free/15">{t('fiscal.stateActive')}</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title={t('fiscal.view')} onClick={() => openDetail(f)} data-testid={`fiscal-view-${f.numero}`}><Eye className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => exportFactura(f, 'xml')} data-testid={`fiscal-xml-${f.numero}`}>{t('fiscal.exportXml')}</Button>
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => exportFactura(f, 'json')}>{t('fiscal.exportJson')}</Button>
                        {!f.anulada && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" title={t('fiscal.anular')} onClick={() => { setAnularTarget(f); setAnularReason(''); }} data-testid={`fiscal-anular-${f.numero}`}><Ban className="h-4 w-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* ---------------- Audit log ---------------- */}
      <Card className="p-5">
        <button type="button" onClick={loadAudit} className="w-full flex items-center justify-between font-semibold" data-testid="fiscal-audit-toggle">
          <span>{t('fiscal.auditTitle')}</span>
          {showAudit ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {showAudit && (
          <div className="mt-4">
            {audit.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">{t('fiscal.noAudit')}</div>
            ) : (
              <ScrollArea className="max-h-72">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('fiscal.auditAction')}</TableHead>
                      <TableHead>{t('fiscal.colNum')}</TableHead>
                      <TableHead>{t('fiscal.auditUser')}</TableHead>
                      <TableHead>{t('fiscal.auditDate')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audit.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell><Badge variant="outline">{a.action}</Badge></TableCell>
                        <TableCell className="font-mono text-xs">{a.num_serie || '—'}</TableCell>
                        <TableCell>{a.user || '—'}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{fmtDate(a.timestamp)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        )}
      </Card>

      {/* ---------------- Detail dialog ---------------- */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="fiscal-detail-dialog">
          <DialogHeader>
            <DialogTitle>{t('fiscal.detailTitle')}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold">{detail.num_serie}</span>
                <Badge variant="outline">{detail.tipo}</Badge>
              </div>
              {detail.qr_base64 && (
                <div className="flex flex-col items-center gap-1">
                  <img src={detail.qr_base64} alt="QR" className="h-40 w-40 rounded-lg border border-border" data-testid="fiscal-detail-qr" />
                  <span className="text-xs text-muted-foreground">{t('fiscal.qrCode')}</span>
                </div>
              )}
              <Separator />
              <div className="grid grid-cols-2 gap-2">
                <div><div className="text-xs text-muted-foreground">{t('fiscal.emisor')}</div><div>{detail.emisor?.nombre} <span className="font-mono">({detail.emisor?.nif})</span></div></div>
                <div><div className="text-xs text-muted-foreground">{t('fiscal.colDate')}</div><div>{detail.fecha_expedicion}</div></div>
                <div><div className="text-xs text-muted-foreground">{t('fiscal.base')}</div><div>{eur(detail.base_imponible)}</div></div>
                <div><div className="text-xs text-muted-foreground">{t('fiscal.vat')}</div><div>{eur(detail.cuota_iva)} ({detail.vat_rate}%)</div></div>
                <div><div className="text-xs text-muted-foreground">{t('fiscal.total')}</div><div className="font-semibold">{eur(detail.importe_total)}</div></div>
              </div>
              <Separator />
              <div>
                <div className="text-xs text-muted-foreground">{t('fiscal.huella')}</div>
                <div className="font-mono text-xs break-all rounded-lg bg-muted/50 p-2">{detail.huella}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{t('fiscal.huellaPrev')}</div>
                <div className="font-mono text-xs break-all rounded-lg bg-muted/50 p-2">
                  {detail.primer_registro === 'S' ? t('fiscal.first') : detail.huella_anterior}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} className="rounded-xl">{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Anular dialog ---------------- */}
      <Dialog open={!!anularTarget} onOpenChange={(o) => !o && setAnularTarget(null)}>
        <DialogContent data-testid="fiscal-anular-dialog">
          <DialogHeader>
            <DialogTitle>{t('fiscal.anularTitle')}</DialogTitle>
            <DialogDescription>{t('fiscal.anularDesc')}</DialogDescription>
          </DialogHeader>
          <div>
            <Label>{t('fiscal.anularReason')}</Label>
            <Textarea value={anularReason} onChange={(e) => setAnularReason(e.target.value)} className="mt-1.5" rows={3} data-testid="fiscal-anular-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnularTarget(null)} className="rounded-xl">{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={confirmAnular} className="rounded-xl" data-testid="fiscal-anular-confirm">{t('fiscal.anularConfirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
